import { useEffect, useRef, useState } from 'react';

/**
 * Reveals streamed text with a smooth typewriter catch-up so chunky
 * SSE updates still feel like continuous human writing.
 */
export function useSmoothedStreamText(targetText, { enabled = true } = {}) {
  const [visible, setVisible] = useState('');
  const targetRef = useRef('');
  const visibleRef = useRef('');
  const rafRef = useRef(0);
  const lastTsRef = useRef(0);

  useEffect(() => {
    const next = String(targetText || '');
    targetRef.current = next;

    const stop = () => {
      if (rafRef.current) {
        cancelAnimationFrame(rafRef.current);
        rafRef.current = 0;
      }
    };

    if (!enabled) {
      stop();
      visibleRef.current = next;
      setVisible(next);
      return stop;
    }

    if (!next) {
      stop();
      visibleRef.current = '';
      setVisible('');
      return stop;
    }

    // Target replaced mid-stream (not a growing prefix) — reset reveal.
    const current = visibleRef.current;
    if (current && !next.startsWith(current)) {
      let shared = 0;
      const limit = Math.min(current.length, next.length);
      while (shared < limit && current[shared] === next[shared]) shared += 1;
      visibleRef.current = next.slice(0, shared);
      setVisible(visibleRef.current);
    }

    const tick = (ts) => {
      const target = targetRef.current;
      let shown = visibleRef.current;

      if (!target) {
        if (shown) {
          visibleRef.current = '';
          setVisible('');
        }
        rafRef.current = 0;
        return;
      }

      if (!target.startsWith(shown)) {
        let shared = 0;
        const limit = Math.min(shown.length, target.length);
        while (shared < limit && shown[shared] === target[shared]) shared += 1;
        shown = target.slice(0, shared);
      }

      if (shown === target) {
        visibleRef.current = shown;
        setVisible(shown);
        rafRef.current = 0;
        return;
      }

      const elapsed = lastTsRef.current ? Math.min(48, ts - lastTsRef.current) : 16;
      lastTsRef.current = ts;
      const remaining = target.length - shown.length;
      // ~50 chars/sec baseline; speed up when the stream is ahead.
      const catchUp = remaining > 120 ? 4 : remaining > 48 ? 2.2 : remaining > 16 ? 1.4 : 1;
      const chars = Math.max(1, Math.ceil((50 * catchUp * elapsed) / 1000));
      shown = target.slice(0, shown.length + chars);
      visibleRef.current = shown;
      setVisible(shown);

      rafRef.current = requestAnimationFrame(tick);
    };

    stop();
    lastTsRef.current = 0;
    rafRef.current = requestAnimationFrame(tick);

    return stop;
  }, [targetText, enabled]);

  return visible;
}
